import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button.tsx"

interface PaginationProps {
    currentPage: number
    totalPages: number
    basePath: string
    pageParam?: string
    query?: Record<string, string | number | null | undefined>
}

export default function Pagination({ currentPage, totalPages, basePath, pageParam = "page", query = {} }: PaginationProps) {
    if (totalPages <= 1) {
        return null
    }

    const buildHref = (page: number) => {
        const searchParams = new URLSearchParams()

        Object.entries(query).forEach(([key, value]) => {
            if (value !== null && value !== undefined && String(value).trim() !== "") {
                searchParams.set(key, String(value))
            }
        })

        searchParams.set(pageParam, String(page))
        return `${basePath}?${searchParams.toString()}`
    }

    const startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(totalPages, currentPage + 2)
    const pages: number[] = []

    for (let page = startPage; page <= endPage; page += 1) {
        pages.push(page)
    }

    return (
        <div className="flex items-center justify-center gap-2">
            {currentPage <= 1 ? (
                <Button variant="outline" size="sm" disabled>
                    Previous
                </Button>
            ) : (
                <Link href={buildHref(currentPage - 1)} preserveScroll>
                    <Button variant="outline" size="sm">
                        Previous
                    </Button>
                </Link>
            )}

            {startPage > 1 && (
                <>
                    <Link href={buildHref(1)} preserveScroll>
                        <Button variant="outline" size="sm">1</Button>
                    </Link>
                    {startPage > 2 && <span className="px-1 text-muted-foreground">...</span>}
                </>
            )}

            {pages.map(page => (
                <Link key={page} href={buildHref(page)} preserveScroll>
                    <Button variant={page === currentPage ? "default" : "outline"} size="sm">
                        {page}
                    </Button>
                </Link>
            ))}

            {endPage < totalPages && (
                <>
                    {endPage < totalPages - 1 && <span className="px-1 text-muted-foreground">...</span>}
                    <Link href={buildHref(totalPages)} preserveScroll>
                        <Button variant="outline" size="sm">{totalPages}</Button>
                    </Link>
                </>
            )}

            {currentPage >= totalPages ? (
                <Button variant="outline" size="sm" disabled>
                    Next
                </Button>
            ) : (
                <Link href={buildHref(currentPage + 1)} preserveScroll>
                    <Button variant="outline" size="sm">
                        Next
                    </Button>
                </Link>
            )}
        </div>
    )
}
